import { Trendbase, ChartOffsets } from "../trend/trendbase.js";
import { Pen } from "../trend/pen.js";
import { getFormattedValue } from "../../lib/utils.js";
import { UiModal } from "../common/modal/modal.js";
import { Legend } from "../trend/legend/legend.js";
/**
 * @class MSChart
 * @extends Trendbase
 * @classdesc График XY
 * */
export class MSChart extends Trendbase {
    static get observedAttributes() {
        return super.observedAttributes.concat(["miny", "maxy"]);
    }
    constructor() {
        super();
        this.checkComponent('ui-modal', UiModal)
        const xdatazoomWidth = this.attributes.width.value - ChartOffsets.left - ChartOffsets.right;
        const shadowRoot = this.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = `
            <style>
                @import "controls/trend/trend.css";
            </style>
            <div class="trend">
                <div class="trend__container" id="trend__container">
                    <ms-toolbar
                        id='toolbar'
                        class="trend__toolbar"
                        style="position: relative;"
                        buttons='[
                            {"method": "Export", "class": "hmi-j-table", "tooltip": "${i18next.t("trend_toolbar_export")}", "type": "button"},
                            {"method": "ShowLegend", "class": ["hmi-j-trend_legend", "hmi-j-trend_legend"], "tooltip": ["${i18next.t('trend_toolbar_minilegenda')}"], "type": "toggle"},
                            {"method": "Print", "class": "hmi-j-print", "tooltip": "${i18next.t("trend_toolbar_print")}", "type": "button"},
                            {"method": "SaveImageAs", "class": "hmi-j-export", "tooltip": "${i18next.t("trend_kits_modal_save")}", "type": "button"}
                        ]'
                    ></ms-toolbar>
                    <div id="chart-wrapper" class="trend__chart-wrapper">
                        <ms-datazoom
                            id="ydatazoom"
                            class="trend__ydatazoom"
                            height="${this.attributes.height.value}"
                            datazoomindex="1"
                            format="f0"
                            width="20"
                            min="0"
                            max="100"
                            minvalue="0"
                            maxvalue="100"
                            foreborderwidth="2"
                            orientation="1"
                        ></ms-datazoom>
                        <div id="chart" class="trend__chart"></div>
                    </div>
                    <ms-datazoom
                        id="datazoom"
                        class="trend__xdatazoom"
                        style="position: relative;"
                        datazoomindex="0"
                        format="f0"
                        width="${xdatazoomWidth}"
                        height="20"
                        orientation="0"
                        foreborderwidth="2"
                    ></ms-datazoom>
                </div>
            </div>
        `;
        this.setParts(shadowRoot);
        this.createTrend();
        //базовый отступ grid для 1 шкалы Y
        this.echarts.setOption({ grid: { left: 45 } });
        let penlimits = JSON.parse(this.attributes.penlimits.value);
        penlimits.max = +this.attributes.maxy.value;
        penlimits.min = +this.attributes.miny.value;
        this._penlimits = penlimits;
        this.createMarkAreaOption(penlimits);
        this.createDataZoom();
        this.initLegend();
        this.msdatazoom.echarts = this.echarts;
        this.msdatazoom.updateForDatazoom = (start, end) => {
            this.echarts &&
                this.echarts.dispatchAction({
                    type: "dataZoom",
                    dataZoomIndex: 0,
                    startValue: start,
                    endValue: end,
                });
        };
        this.msydatazoom.echarts = this.echarts;
        this.msydatazoom.min = this.miny;
        this.msydatazoom.max = this.maxy;
        this.msydatazoom.updateForDatazoom = (start, end) => {
            this.echarts &&
                this.echarts.dispatchAction({
                    type: "dataZoom",
                    dataZoomIndex: 1,
                    startValue: start,
                    endValue: end,
                });
        };
        this.echarts.setOption({
            xAxis: {
                type: "value",
                scale: true,
                axisLabel: {
                    color: "#000000",
                    formatter: (value, index) => {
                        return getFormattedValue(
                            this._xformat,
                            value,
                            null,
                            null,
                            true
                        );
                    },
                },
                axisPointer: {
                    label: {
                        formatter: (value, index) => {
                            return getFormattedValue(
                                this._xformat,
                                value.value,
                                null,
                                null,
                                true
                            );
                        },
                    },
                },
            },
            yAxis: {
                type: "value",
                scale: true,
                axisLabel: {
                    formatter: (value, index) => {
                        return getFormattedValue(
                            this._yformat,
                            value,
                            null,
                            null,
                            true
                        );
                    },
                },
                axisPointer: {
                    label: {
                        formatter: (value, index) => {
                            return getFormattedValue(
                                this._yformat,
                                value.value,
                                null,
                                null,
                                true
                            );
                        },
                    },
                },
            },
        });
        this.echarts.on("datazoom", () => {
            this.msdatazoom.minvalue = this.datazoom.startValue;
            this.msdatazoom.maxvalue = this.datazoom.endValue;
            this.msydatazoom.minvalue = this.ydatazoom.startValue;
            this.msydatazoom.maxvalue = this.ydatazoom.endValue;
        });
        this.toolbar.context = this;

        this.updateMsydatazoomHeight();
    }

    initLegend() {
        this.legend = new Legend(this);
    }

    setScope() {
        const curPen = this.pens[this.currentPen]
        const pen = curPen?.isvisible ? curPen : this.pens.find(pen => pen.isvisible)
        this.currentPen = this.currentPen === pen?.idx ? this.currentPen : pen?.idx
    }

    updateMsydatazoomHeight() {
        const msdatazoomHeight = this.msdatazoom.isvisible
            ? this.msdatazoom.height
            : 0;
        const { top, bottom } = ChartOffsets;
        this.msydatazoom.height =
            this.attributes.height.value -
            parseFloat(this.toolbar.height) -
            msdatazoomHeight -
            top -
            bottom;
    }

    changePenAttribute(attr, value) {
        let index = +attr.split(".")[1];
        let parameter = attr.split(".")[2];
        this.pens[index][parameter.toLowerCase()] = value;
        this.echarts.setOption({
            series: this.pens.series,
            yAxis: this.pens.yaxises,
        });
    }

    createDataZoom() {
        this.echarts.setOption({
            dataZoom: [
                {
                    type: "inside",
                    filterMode: "none",
                    xAxisIndex: 0,
                },
                {
                    type: "inside",
                    filterMode: "none",
                    yAxisIndex: 0,
                },
            ],
        });

        const [dataZoom, yDataZoom] = this.echarts.getModel().option.dataZoom;
        this.datazoom = dataZoom;
        this.ydatazoom = yDataZoom;
    }
    addDataSource() { }
    dummy() {
        return;
    }
    onStringPenChanged(path, value) {
        let i = path.split(".")[1];
        this.pens.series[i].data = value;
        this.echarts.setOption({
            series: this.pens.series,
        });
        this.msdatazoom.min = this.datazoom.startValue;
        this.msdatazoom.max = this.datazoom.endValue;
        this.msdatazoom.minvalue = this.datazoom.startValue;
        this.msdatazoom.maxvalue = this.datazoom.endValue;
    }
    createYAxisesOption() {
        if (this.attributes.data.value === "") {
            this.pens.yaxises = {};
            return;
        }
        this.pens.yaxises = {
            type: "value",
            splitLine: {
                lineStyle: {
                    color: this.attributes.gridfill.value,
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        width: +this.attributes.axisthicknessy.value,
                    },
                },
                axisTick: {
                    show: true,
                    lineStyle: {
                        width: +this.attributes.tickheighty.value,
                        color: this.attributes.tickcolory.value,
                        length: +this.attributes.tickwidthy.value,
                    },
                },
            },
            min: +this.attributes.miny.value,
            max: +this.attributes.maxy.value,
        };
    }
    createSeriesOption() {
        this.pens = [];
        this.pens.series = [];
        try {
            const pens = JSON.parse(this.attributes.data.value);
            let index = 0;
            for (let penname in pens) {
                let name = pens[penname].name;
                pens[penname].penname = name;
                this.pens.push(new Pen(this, pens[penname]));
                this.pens[index].updateYAxisesOffset = this.dummy;
                this.pens[index].updateSeriesAxises = this.dummy;
                this.pens[index].broadcast = this.dummy;
                this.pens[index].source = name;
                this.pens[index]._echarts = this.echarts;
                this.pens[index].minilegend = this.legend;
                this.pens[index].idx = index;
                this.pens.series.push(this.pens[index].serie);
                index++;
            }
        } catch (e) {
            $ns.add({
                type: "info",
                time: new Date().toLocaleString(),
                title: i18next.t("chart_XY_chart_is_empty"),
                text: String.format(i18next.t("chartXY_missing_pens"), this.id),
            });
        }
    }
    applyMark(markarea, markline) {
        if (markarea.length > 0)
            this.echarts.setOption({
                series: {
                    markLine: { data: markline },
                    markArea: { silent: true, data: markarea },
                },
            });
    }

    getYAxis() {
        return this.pens.yaxises.length ? this.pens.yaxises : {}
    }

    get height() {
        return super.height;
    }
    set height(value) {
        if (value === undefined) return
        super.height = value;
        setTimeout(() => {
            const height = +value - this.toolbar.offsetHeight;
            if (isNaN(height)) return
            this.chartContainer.height = height
            if (this.echarts) this.echarts.resize({ height })
            this.updateMsydatazoomHeight();
        }, 230) // таймер нужен, чтобы компонент успел обновить высоту
    }

    set textcolor(value) {
        value = this.tryToGetUserColor(value);
        this.echarts.setOption({ title: { textStyle: { color: value } } });
        this.echarts.setOption({ xAxis: { axisLabel: { color: value } } });
        this.echarts.setOption({ xAxis: { nameTextStyle: { color: value } } });
        this.echarts.setOption({ yAxis: { axisLabel: { color: value } } });
    }
    get miny() {
        return this._miny;
    }
    set miny(value) {
        this._miny = +value;
        this.msydatazoom.min = this._miny;
        this.msydatazoom.minvalue = this._miny;
        this.pens.yaxises.min = this._miny;
        this._penlimits.min = this._miny;
        this.createMarkAreaOption(this._penlimits);
        this.echarts.setOption({ yAxis: this.pens.yaxises });
    }
    get maxy() {
        return this._maxy;
    }
    set maxy(value) {
        this._maxy = +value;
        this.msydatazoom.max = this._maxy;
        this.msydatazoom.maxvalue = this._maxy;
        this.pens.yaxises.max = this._maxy;
        this._penlimits.max = this._maxy;
        this.createMarkAreaOption(this._penlimits);
        this.echarts.setOption({ yAxis: this.pens.yaxises });
    }

    /**
     * Обновить перья и оси в echarts
     * */
    updateSeriesAxises() {
        //этот метод обязан быть, так как вызывается из Pen
    }
}